package ui

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import data.FoodSafetyEntry
import org.jetbrains.compose.web.attributes.ButtonType
import org.jetbrains.compose.web.attributes.type
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.css.keywords.auto
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.HTMLElement

// =====================
// Tabs (Vertical → Mobile Horizontal via AppStyles)
// =====================
@Composable
fun VerticalVerdictTabs(
    selectedTab: String,
    onSelect: (String) -> Unit
) {
    val tabs = listOf(
        "all" to "전체",
        "safe" to "안전",
        "conditional" to "조건부",
        "caution" to "주의",
        "avoid" to "금지"
    )

    Div({ classes(AppStyles.verdictTabs) }) {
        tabs.forEach { (key, label) ->
            val isSelected = selectedTab == key

            Button(attrs = {
                classes(AppStyles.verdictTabButton)
                onClick { onSelect(key) }
                style {
                    if (isSelected) {
                        backgroundColor(verdictColor(key))
                        color(Color.white)
                        border(1.px, LineStyle.Solid, verdictColor(key))
                        fontWeight("700")
                    } else {
                        backgroundColor(Color.white)
                        color(Color("#111827"))
                        border(1.px, LineStyle.Solid, Color("#e5e7eb"))
                    }
                }
            }) { Text(label) }
        }
    }
}

// =====================
// Source List
// =====================
@Composable
fun SourceListTab(entries: List<FoodSafetyEntry>) {
    // 1. 상태 변수 추가: 기본값은 false (접힘 상태)
    var showAll by remember { mutableStateOf(false) }

    // 2. 보여줄 데이터 결정 (전체보기 모드면 모두, 아니면 최대 5개)
    val displayEntries = if (showAll) entries else entries.take(5)

    // flex 아이템이 줄어들 수 있게 감싸는 Div
    Div({
        style {
            property("flex", "1 1 0")
            minWidth(0.px)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
        }
    }) {
        Ul({
            style {
                listStyle("none")
                padding(0.px)
                margin(0.px)
            }
        }) {
            if (entries.isEmpty()) {
                Li({
                    style {
                        color(Color("#6b7280"))
                        fontSize(14.px)
                        padding(6.px, 0.px)
                    }
                }) { Text("해당 분류의 출처 정보가 없습니다.") }
            } else {
                // 제한된 개수만큼만 출력
                displayEntries.forEach { entry -> SourceLine(entry) }
            }
        }

        // 3. 데이터가 5개보다 많을 때만 버튼 표시
        if (entries.size > 5) {
            Button({
                type(ButtonType.Button)
                onClick {
                    showAll = !showAll
                }
                style {
                    width(100.percent)
                    padding(10.px)
                    marginTop(8.px)
                    backgroundColor(Color("#f3f4f6")) // 연한 회색 배경
                    border(1.px, LineStyle.Solid, Color("#e5e7eb"))
                    borderRadius(8.px)
                    cursor("pointer")
                    fontSize(13.px)
                    fontWeight("600")
                    color(Color("#4b5563"))
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.Center)
                    alignItems(AlignItems.Center)
                    gap(4.px)
                }
            }) {
                if (showAll) {
                    Text("접기 ▲")
                } else {
                    Text("출처 전체보기 (+${entries.size - 5}개) ▼")
                }
            }
        }
    }
}
//@Composable
//fun SourceListTab(entries: List<FoodSafetyEntry>) {
//    // ✅ flex 아이템이 줄어들 수 있게
//    Div({
//        style {
//            property("flex", "1 1 0")
//            minWidth(0.px)
//        }
//    }) {
//        Ul({
//            style {
//                listStyle("none")
//                padding(0.px)
//                margin(0.px)
//            }
//        }) {
//            if (entries.isEmpty()) {
//                Li({
//                    style {
//                        color(Color("#6b7280"))
//                        fontSize(14.px)
//                        padding(6.px, 0.px)
//                    }
//                }) { Text("해당 분류의 출처 정보가 없습니다.") }
//            } else {
//                entries.forEach { entry -> SourceLine(entry) }
//            }
//        }
//    }
//}

/**
 * ✅ 핵심 수정:
 * - 말줄임은 "텍스트가 들어있는 요소"에 직접 걸어야 안정적임
 * - A는 Div/B로 감싸지 말고 A가 Text를 직접 가지게 만들기
 * - flex: 1 1 0 + min-width:0 조합이 모바일에서 overflow를 확실히 막음
 */
@Composable
fun SourceLine(entry: FoodSafetyEntry) {
    val href = entry.url?.takeIf { it.isNotBlank() }

    Li({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(8.px)
            marginBottom(8.px)
            width(100.percent)
            property("min-width", "0")
        }
    }) {
        // [1] 배지 (고정)
        Span({
            style {
                flexShrink(0)
                backgroundColor(verdictBgRgba(entry.verdict, 0.15f))
                color(verdictColor(entry.verdict))
                padding(2.px, 6.px); borderRadius(6.px); fontSize(12.px)
                property("white-space", "nowrap")
            }
        }) { Text(verdictLabel(entry.verdict)) }

        // [2] 텍스트 + 아이콘 영역
        if (href != null) {
            A(href = href, attrs = {
                attr("target", "_blank")
                style {
                    display(DisplayStyle.Flex) // Flex로 변경
                    alignItems(AlignItems.Center)
                    property("flex", "1 1 0px") // 남은 공간 차지
                    property("min-width", "0")
                    textDecoration("none")
                }
            }) {
                // 실제 텍스트 부분만 잘리도록 설정
                Span({
                    style {
                        color(Color("#2563eb"))
                        fontSize(14.px); fontWeight("bold")
                        property("overflow", "hidden")
                        property("text-overflow", "ellipsis") // 여기서 ... 발생
                        property("white-space", "nowrap")
                        flexGrow(1)
                    }
                }) { Text(entry.source) }

                // 아이콘은 텍스트 바로 뒤에 붙지만 절대 안 잘림
                Span({
                    style {
                        marginLeft(4.px)
                        fontSize(12.px)
                        flexShrink(0) // 🔴 핵심: 절대 숨겨지거나 작아지지 않음
                    }
                }) { Text("🔗") }
            }
        } else {
            // 링크가 없는 경우 (텍스트만)
            Span({
                style {
                    flex(1, 1, 0.px)
                    fontSize(14.px); fontWeight("bold")
                    property("overflow", "hidden")
                    property("text-overflow", "ellipsis")
                    property("white-space", "nowrap")
                }
            }) { Text(entry.source) }
        }

        // [3] 신뢰도 바 (오른쪽 끝 고정)
        // [3] 신뢰도 글자 + 바 영역 (오른쪽 고정)
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(6.px)
                flexShrink(0)
                marginLeft(4.px)
            }
        }) {
            Span({
                style {
                    fontSize(11.px)
                    color(Color("#6b7280"))
                    property("white-space", "nowrap") // 글자 줄바꿈 방지
                }
            }) { Text("신뢰도") }

            ReliabilityBar(entry.reliability)
        }
    }
}

// =====================
// Tri-group recommendation (3 → 2 → 1 columns via AppStyles.triGroupGrid)
// =====================
private data class Signal(val icon: String, val label: String, val bg: String, val fg: String)

private fun decideSignal(
    safePct: Double,
    conditionalPct: Double,
    cautionPct: Double,
    avoidPct: Double
): Signal = when {
    avoidPct >= 40.0 ->
        Signal("✖️", "피하는 게 좋아요", "#fee2e2", "#b91c1c")
    cautionPct >= 40.0 ->
        Signal("⚠️", "주의가 필요해요", "#fef3c7", "#92400e")
    conditionalPct >= 40.0 ->
        Signal("🟡", "조심만 하시면 괜찮아요", "#e0f2fe", "#075985")
    else ->
        Signal("💚", "대체로 괜찮아요", "#dcfce7", "#166534")
}

private fun isHigh(e: FoodSafetyEntry) = e.reliability >= 3.0
private fun isMid(e: FoodSafetyEntry) = e.reliability >= 2.0 && e.reliability < 3.0
private fun isLow(e: FoodSafetyEntry) = e.reliability >= 1.0 && e.reliability < 2.0

@Composable
private fun GroupRecommendationCell(
    reliability: String,
    title: String,
    entries: List<FoodSafetyEntry>
) {
    val total = entries.size

    // ✅ total==0 / else 를 return 없이 분기 (return 에러 방지)
    if (total == 0) {
        Div({
            classes(AppStyles.triGroupItem)
            style { backgroundColor(Color("#f9fafb")) }
        }) {
            Span({ classes(AppStyles.triBadge); style { color(Color("#6b7280")) } }) { Text(reliability) }
            Span({ classes(AppStyles.triGroupTitle) }) { Text(title) }

            Div({ classes(AppStyles.triGroupIcon) }) { Text("？") }
            Span({ classes(AppStyles.triGroupMainLine); style { color(Color("#9ca3af")) } }) { Text("자료 없음") }
        }
    } else {
        val safe = entries.count { it.verdict.equals("safe", true) }
        val conditional = entries.count { it.verdict.equals("conditional", true) }
        val caution = entries.count { it.verdict.equals("caution", true) }
        val avoid = entries.count { it.verdict.equals("avoid", true) }

        fun pct(n: Int) = n * 100.0 / total
        val signal = decideSignal(
            safePct = pct(safe),
            conditionalPct = pct(conditional),
            cautionPct = pct(caution),
            avoidPct = pct(avoid)
        )

        Div({
            classes(AppStyles.triGroupItem)
            style { backgroundColor(Color(signal.bg)) }
        }) {
            // 신뢰도 라벨
            Span({
                classes(AppStyles.triBadge)
                style {
                    color(
                        when (reliability) {
                            "신뢰도 높음" -> Color("#166534")
                            "신뢰도 중간" -> Color("#92400e")
                            else -> Color("#991b1b")
                        }
                    )
                }
            }) { Text(reliability) }

            // 타이틀
            Span({ classes(AppStyles.triGroupTitle) }) { Text(title) }

            // 아이콘
            Div({
                classes(AppStyles.triGroupIcon)
                style { color(Color(signal.fg)) }
                title(signal.label)
            }) { Text(signal.icon) }

            // 메인 라인
            Span({
                classes(AppStyles.triGroupMainLine)
                style { color(Color(signal.fg)) }
            }) { Text(signal.label) }

            // 상세 라인 (모바일에서 Styles.kt가 숨김 처리)
            Span({ classes(AppStyles.triGroupDetailLine) }) {
                Text("안전 $safe · 조건부 $conditional · 주의 $caution · 금지 $avoid")
            }
        }
    }
}

@Composable
fun TriGroupRecommendationRow(entries: List<FoodSafetyEntry>) {
    Div({ classes(AppStyles.triGroupGrid) }) {
        val high = entries.filter(::isHigh)
        val mid = entries.filter(::isMid)
        val low = entries.filter(::isLow)

        GroupRecommendationCell("신뢰도 높음", "의료인 / 전문기관", high)
        GroupRecommendationCell("신뢰도 중간", "기업 / 보도자료", mid)
        GroupRecommendationCell("신뢰도 낮음", "개인 / 커뮤니티", low)
    }
}

// =====================
// Reliability-aware summary
// =====================
private enum class Tier { HIGH, MID, LOW }

private data class VerdictCounts(
    val safe: Int,
    val conditional: Int,
    val caution: Int,
    val avoid: Int
) {
    val total get() = safe + conditional + caution + avoid
    fun pct(n: Int) = if (total == 0) 0.0 else n * 100.0 / total
}

private fun tierOf(rel: Double): Tier = when {
    rel >= 3.0 -> Tier.HIGH
    rel >= 2.0 -> Tier.MID
    else -> Tier.LOW
}

private enum class Dominance { SAFE, CONDITIONAL, CAUTION, BAN, MIXED }

private fun dominanceOf(c: VerdictCounts): Dominance {
    if (c.total == 0) return Dominance.MIXED

    val safePct = c.pct(c.safe)
    val condPct = c.pct(c.conditional)
    val cautionPct = c.pct(c.caution)
    val avoidPct = c.pct(c.avoid)

    return when {
        avoidPct >= 50.0 -> Dominance.BAN
        cautionPct >= 40.0 || (avoidPct >= 30.0 && (cautionPct + avoidPct) >= 50.0) -> Dominance.CAUTION
        condPct >= 40.0 -> Dominance.CONDITIONAL
        safePct >= 60.0 -> Dominance.SAFE
        else -> Dominance.MIXED
    }
}

fun buildReliabilityAwareSummary(entries: List<FoodSafetyEntry>): String {
    if (entries.isEmpty()) return ""

    val countsByTier: Map<Tier, VerdictCounts> = Tier.values().associateWith { tier ->
        val list = entries.filter { tierOf(it.reliability) == tier }
        VerdictCounts(
            safe = list.count { it.verdict.equals("safe", true) },
            conditional = list.count { it.verdict.equals("conditional", true) },
            caution = list.count { it.verdict.equals("caution", true) },
            avoid = list.count { it.verdict.equals("avoid", true) }
        )
    }

    val primary: Tier? = listOf(Tier.HIGH, Tier.MID, Tier.LOW).firstOrNull { countsByTier[it]!!.total > 0 }
    val pCounts = primary?.let { countsByTier[it] } ?: VerdictCounts(0, 0, 0, 0)

    fun tierLabel(t: Tier) = when (t) {
        Tier.HIGH -> "신뢰도 높은 출처에 따르면"
        Tier.MID -> "신뢰도 중간 수준 출처에 따르면"
        Tier.LOW -> "신뢰도가 낮은 출처에 따르면"
    }

    val dom = dominanceOf(pCounts)

    val head = when (dom) {
        Dominance.SAFE -> "${tierLabel(primary!!)} 대체로 괜찮다는 의견이 많아요."
        Dominance.CONDITIONAL -> "${tierLabel(primary!!)} 조심만 하시면 드셔도 괜찮다는 의견이 많아요."
        Dominance.CAUTION -> "${tierLabel(primary!!)} 주의가 필요하다는 의견이 우세해요."
        Dominance.BAN -> "${tierLabel(primary!!)} 피하라는 의견이 우세해요."
        Dominance.MIXED -> "${tierLabel(primary!!)} 의견이 갈려요."
    }

    val others = Tier.values().filter { it != primary }
    val otherTotal = others.sumOf { countsByTier[it]!!.total }.coerceAtLeast(1)
    val otherSafe = others.sumOf { countsByTier[it]!!.safe }
    val otherCond = others.sumOf { countsByTier[it]!!.conditional }
    val otherCau = others.sumOf { countsByTier[it]!!.caution }
    val otherAvoid = others.sumOf { countsByTier[it]!!.avoid }

    fun notable(n: Int) = n >= 2 || (n * 100.0 / otherTotal) >= 30.0

    val tail = when (dom) {
        Dominance.BAN -> when {
            notable(otherSafe) || notable(otherCond) -> " 다만, 다른 출처에서는 조건부/괜찮다는 의견도 일부 있어요."
            else -> ""
        }
        Dominance.CAUTION -> when {
            notable(otherSafe) -> " 다만, 괜찮다는 의견도 일부 있어요."
            notable(otherCond) -> " 다만, 조건만 지키면 괜찮다는 의견도 일부 있어요."
            else -> ""
        }
        Dominance.CONDITIONAL -> when {
            notable(otherCau) || notable(otherAvoid) -> " 다만, 주의가 필요하다는 의견도 일부 있어요."
            notable(otherSafe) -> " 또, 괜찮다는 의견도 일부 있어요."
            else -> ""
        }
        Dominance.SAFE -> when {
            notable(otherAvoid) || notable(otherCau) -> " 다만, 주의·피하라는 의견도 일부 있어요."
            notable(otherCond) -> " 다만, 조건부 의견도 일부 있어요."
            else -> ""
        }
        Dominance.MIXED -> ""//" (안전 ${pCounts.safe} · 조건부 ${pCounts.conditional} · 주의 ${pCounts.caution} · 금지 ${pCounts.avoid})"
    }

    return head + tail
}

// =====================
// Reliability bar + Disclaimer
// =====================
@Composable
fun ReliabilityBar(rel: Double) {
    val level = when {
        rel >= 3.0 -> 3
        rel >= 2.0 -> 2
        else -> 1
    }
    val fillWidth = (level * 10)

    val fillColor = when (level) {
        3 -> "#16a34a"
        2 -> "#d97706"
        else -> "#dc2626"
    }

    Div({
        style {
            width(30.px); height(8.px)
            borderRadius(999.px)
            backgroundColor(Color("#e5e7eb"))
            position(Position.Relative)
            property("flex-shrink", "0")
        }
        title("신뢰도 ${fmt1(rel)}")
    }) {
        Div({
            style {
                width(fillWidth.px); height(8.px)
                borderRadius(999.px)
                backgroundColor(Color(fillColor))
                position(Position.Absolute)
                left(0.px); top(0.px)
            }
        })
    }
}

